/**
 * taxSettingsSnapshotDiff — compare two save-history snapshots of Tax Planning Settings V2.
 *
 * Unlike the Question/Calculate slices, a settings version is ONE ROW (~85 config + prompt columns,
 * see `Model/AppModel/TaxPlanningSettingsV2Models.cs`), so a snapshot's `payloadJson` is a single
 * object, not a list. The diff is therefore field-level from the start: there is nothing to match by
 * natural key — the column name IS the identity.
 *
 * Three ideas carry the whole file:
 *
 *   1. No hard-coded column list. Every key on either side is compared, so a column added to
 *      `tax.TaxPlanningSettingsV2` later shows up in the compare dialog on its own. (The docs in
 *      `taxPlanningSettingsV2Api.js` that still say "57 fields" are exactly the kind of stale list
 *      this avoids.)
 *
 *   2. 🔴 `null` IS DATA. A null column means "use the default written in the code" — it is NOT an
 *      empty value, and `TaxPromptStackV2.BuildStack` / `TaxPlaygroundV2BAL.EnrichPromptProvenance`
 *      decide `config:` vs `default` on exactly that emptiness. So `null` and `''` must read as a
 *      real change here (an admin who cleared a textarea to "" has NOT restored the code default),
 *      and null is rendered with a sentence a human can read instead of a blank cell.
 *
 *   3. The prompt columns are long multi-line text. Line endings are not content: a version that
 *      travelled through a CRLF checkout must not read as "every prompt edited", so newlines are
 *      normalized to `\n` before anything is compared.
 *
 * Pure logic — NO Vue/Vuetify import — so it runs under plain Node for the unit tests.
 *   Test:  cd WebAPI/ClientApp && node --test tests/taxSettingsSnapshotDiff.test.mjs
 */

/**
 * Columns that must never read as a change: the row's identity, the single-active flag, and the
 * audit stamps. `versionCode` / `isActive` are excluded on purpose — restore writes the config
 * columns back into the CURRENT row and deliberately leaves those three alone, so a diff that
 * counted them would report a change restore can never make.
 * Compared case-insensitively (a payload captured before the camelCase policy can be PascalCase).
 */
export const IGNORED_FIELDS = Object.freeze([
  'id',
  'versionCode',
  'isActive',
  'createdBy',
  'createdDate',
  'updatedBy',
  'updatedDate',
]);

const IGNORED = new Set(IGNORED_FIELDS.map((f) => f.toLowerCase()));

/** What a null column is rendered as. Exported so the dialog labels it the same way. */
export const NULL_LABEL = 'ใช้ default ในโค้ด';

/**
 * Comparison sentinel for null. It carries NUL characters, which JSON cannot deliver inside a
 * string unescaped and no admin can type, so a real column value can never collide with it —
 * that is what keeps `null` and `''` telling apart.
 */
const NULL_TOKEN = '\u0000<null>\u0000';

/** Case-insensitive property read — exact hit first (the normal path), then a scan. */
function getProp(obj, name) {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, name)) return obj[name];
  const lname = String(name).toLowerCase();
  const hit = Object.keys(obj).find((k) => k.toLowerCase() === lname);
  return hit === undefined ? undefined : obj[hit];
}

/** CRLF → LF. A line ending is not an edit; the prompt columns are the reason this exists. */
function normalizeNewlines(s) {
  return s.indexOf('\r') === -1 ? s : s.replace(/\r\n/g, '\n');
}

/**
 * object/array → JSON · everything else → String(). null/undefined never reach here with a
 * meaning of their own — `compareValue`/`displayValue` intercept them first.
 * Inside a JSON blob a CRLF is escaped as `\r\n`, so the escaped form is folded on that path too.
 */
function normalizeValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try {
      return normalizeNewlines(JSON.stringify(v)).replace(/\\r\\n/g, '\\n');
    } catch {
      return String(v);
    }
  }
  return normalizeNewlines(String(v));
}

/** True for a value the database stored as NULL (and for a key present with an undefined value). */
function isNullish(v) {
  return v === null || v === undefined;
}

/** The string two sides are compared on — null gets its own token so `null !== ''`. */
function compareValue(v) {
  return isNullish(v) ? NULL_TOKEN : normalizeValue(v);
}

/** The string the dialog shows — null becomes a sentence, everything else its normalized text. */
export function displayValue(v) {
  return isNullish(v) ? NULL_LABEL : normalizeValue(v);
}

/**
 * Display form of a value, cut at `max` with an ellipsis — the prompt columns run to thousands of
 * characters and are compared whole but must not be rendered whole.
 */
export function truncateValue(v, max = 300) {
  const s = displayValue(v);
  if (!(max > 0)) return '';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * `payloadJson` → the settings record. Accepts the raw string off the API, an already-parsed object,
 * null/'' , or complete garbage — a broken payload yields an empty diff, never a throw, because this
 * runs inside a dialog the admin opened to LOOK at history.
 *
 * A payload wrapped as `{ settings: { … } }` is unwrapped: no settings column is called `settings`,
 * so the shape is unambiguous, and this keeps the util working whichever way the capture side spells
 * the envelope.
 */
export function parseSettingsSnapshotPayload(payloadJson) {
  let raw = payloadJson;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return {};
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const inner = getProp(raw, 'settings');
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) return inner;
  return raw;
}

/**
 * Union of the comparable keys of both records, as `lowercase → the casing to display`.
 * `first` is consulted first so the current (right) side decides the casing and the field order.
 */
function comparableKeys(first, second) {
  const seen = new Map();
  [first, second].forEach((row) => {
    if (!row) return;
    Object.keys(row).forEach((k) => {
      const lk = k.toLowerCase();
      if (IGNORED.has(lk) || seen.has(lk)) return;
      seen.set(lk, k);
    });
  });
  return seen;
}

/** Case-insensitive "does this record carry that column at all". */
function hasKey(obj, lname) {
  if (!obj) return false;
  return Object.keys(obj).some((k) => k.toLowerCase() === lname);
}

/**
 * Compare two settings snapshot payloads (older on the left, newer on the right).
 *
 * → `{ summary: { added, removed, modified, unchanged },
 *      fields: [ { name, state, left, right, leftIsNull, rightIsNull } ] }`
 *
 * `fields` carries EVERY comparable column, unchanged ones included (state `'unchanged'`), in the
 * right-hand record's own order followed by the columns only the left record had — the dialog
 * filters on `state` for its "show unchanged" toggle, exactly like the Question V2 compare does.
 *
 *   · `added`   — the column exists only on the right (a column introduced since the older save)
 *   · `removed` — the column exists only on the left
 *   · `modified`/`unchanged` — present on both; `null` and `''` are DIFFERENT values here (see the
 *     file header), and `leftIsNull`/`rightIsNull` let the dialog style the null side as the
 *     "using the code default" state rather than as blank text.
 */
export function diffSettingsSnapshots(leftPayloadJson, rightPayloadJson) {
  const left = parseSettingsSnapshotPayload(leftPayloadJson);
  const right = parseSettingsSnapshotPayload(rightPayloadJson);

  const fields = [];
  comparableKeys(right, left).forEach((display, lk) => {
    const inLeft = hasKey(left, lk);
    const inRight = hasKey(right, lk);
    const lv = getProp(left, lk);
    const rv = getProp(right, lk);

    let state;
    if (!inLeft) state = 'added';
    else if (!inRight) state = 'removed';
    else state = compareValue(lv) === compareValue(rv) ? 'unchanged' : 'modified';

    fields.push({
      name: display,
      state,
      left: inLeft ? displayValue(lv) : '',
      right: inRight ? displayValue(rv) : '',
      leftIsNull: inLeft && isNullish(lv),
      rightIsNull: inRight && isNullish(rv),
    });
  });

  const summary = { added: 0, removed: 0, modified: 0, unchanged: 0 };
  fields.forEach((f) => {
    summary[f.state] += 1;
  });

  return { summary, fields };
}
